import { Token } from '@/domain/types/Token'
import { assets, getTokenImage } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { GrowingReward } from '@/ui/molecules/growing-reward/GrowingReward'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { NormalizedUnitNumber } from '@sparkdotfi/common-universal'
import { ReactNode } from 'react'

export interface StakingRewardsPanelProps {
  claimableRewards: NormalizedUnitNumber
  stakedAmount: NormalizedUnitNumber
  rewardToken: Token
  stakingToken: Token
  calculateReward: (timestampInMs: number) => NormalizedUnitNumber
  refreshGrowingRewardIntervalInMs: number | undefined
  isRewardOutOfSync: boolean
  openClaimDialog: () => void
  openUnstakeDialog: () => void
  openStakeDialog: () => void
  stakingMultiplier: NormalizedUnitNumber
  className?: string
}

export function StakingRewardsPanel({
  claimableRewards,
  stakedAmount,
  rewardToken,
  stakingToken,
  calculateReward,
  refreshGrowingRewardIntervalInMs,
  isRewardOutOfSync,
  openClaimDialog,
  openUnstakeDialog,
  openStakeDialog,
  stakingMultiplier,
  className,
}: StakingRewardsPanelProps) {
  return (
    <Panel
      spacing="m"
      className={cn(
        'flex flex-col justify-between gap-8 lg:gap-16',
        'bg-cover bg-right bg-no-repeat',
        'bg-[url(/src/ui/assets/spark-staking/staking-rewards-panel-bg.svg)] bg-primary-inverse',
        className,
      )}
    >
      <div className="flex w-full flex-row items-center justify-between">
        <div className="typography-heading-4 flex items-baseline gap-1 text-primary-inverse">
          My Spark Points{' '}
          <Info className="icon-secondary">
            Spark Points accrued for staking SPK. Points are tracked off-chain and are not claimable as tokens.
          </Info>
        </div>
        <div className={cn('grid gap-1', claimableRewards.gt(0) ? 'grid-cols-[80px_80px]' : 'grid-cols-[80px]')}>
          {claimableRewards.gt(0) && (
            <Button onClick={openClaimDialog} variant={stakedAmount.gt(0) ? 'primary' : 'secondary'} size="s">
              Claim
            </Button>
          )}
          <Button
            onClick={stakedAmount.gt(0) ? openUnstakeDialog : openStakeDialog}
            variant={stakedAmount.gt(0) ? 'secondary' : 'primary'}
            size="s"
          >
            {stakedAmount.gt(0) ? 'Unstake' : 'Stake'}
          </Button>
        </div>
      </div>
      <GrowingReward
        rewardToken={rewardToken}
        iconSrc={assets.sparkIcon}
        calculateReward={calculateReward}
        refreshIntervalInMs={refreshGrowingRewardIntervalInMs}
        isOutOfSync={isRewardOutOfSync}
        wholePartTextBgGradientClass={cn(
          'bg-[radial-gradient(160.27%_160.27%_at_50.08%_115.91%,_#FFD232_0%,_#FF6D6D_100%)]',
        )}
        fractionalPartTextColorClass="text-[#FFAD48]"
      />
      <div className="flex divide-x divide-fg-secondary">
        <DetailsItem label="My Stake" tooltip="The amount of SPK you have staked.">
          <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">
            <img src={getTokenImage(stakingToken.symbol)} className="h-4" alt={stakingToken.symbol} />
            {stakingToken.format(stakedAmount, { style: 'auto' })}
          </div>
        </DetailsItem>
        <DetailsItem label="Multiplier" tooltip="Spark Points multiplier applied to your staked SPK.">
          <div className="typography-label-1 bg-gradient-spark-primary-2 bg-clip-text text-transparent">
            {formatMultiplier(stakingMultiplier)}x
          </div>
        </DetailsItem>
        {claimableRewards.gt(0) && (
          <DetailsItem label="Available to claim" tooltip="Rewards that can be claimed now.">
            <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">
              <img src={getTokenImage(rewardToken.symbol)} className="h-4" alt={rewardToken.symbol} />
              {rewardToken.format(claimableRewards, { style: 'auto' })}
            </div>
          </DetailsItem>
        )}
      </div>
    </Panel>
  )
}

function formatMultiplier(multiplier: NormalizedUnitNumber): string {
  return Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(multiplier.toNumber())
}

function DetailsItem({ label, tooltip, children }: { label: string; tooltip: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 px-3 first:pl-0 last:pr-0 xl:px-6">
      <div className="typography-label-3 flex items-center gap-1 text-tertiary">
        {label} <Info className="icon-secondary">{tooltip}</Info>
      </div>
      <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">{children}</div>
    </div>
  )
}
